import type { WizardRef } from '@myosh/odin-components';
import { Wizard, WizardStep } from '@myosh/odin-components';
import type { Ref } from 'react';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WrapperStyledButtonField from '../../common/wrapper-styled-button-field';
import type { CloneWizardState } from './clone-record-button-field.component';
import { initialCloneWizardState } from './clone-record-button-field.component';
import FirstStep from './first-step';
import SecondStep from './second-step';
import ThirdStep from './third-step';

export interface CloneWizardRef {
  open: () => void;
}

interface CloneRecordWizzardProps {
  buttonCaption?: string;
  disabled?: boolean;
  onClick: () => void;
  formId: number;
  cloneWizardTargetPersonField?: number;
  cloneWizardTargetWorkflowStep?: number;
  allowUserAttachmentCloning?: boolean;
  moduleFormCloneTargetId?: number;
  cloneWizardState: CloneWizardState;
  setCloneWizardState: React.Dispatch<React.SetStateAction<CloneWizardState>>;
  showSaveConfirmationDialog: () => void;
  isNewRecord: boolean;
}

const CloneRecordWizzard = (
  {
    buttonCaption,
    disabled,
    onClick,
    formId,
    cloneWizardTargetPersonField,
    cloneWizardTargetWorkflowStep,
    allowUserAttachmentCloning,
    moduleFormCloneTargetId,
    cloneWizardState,
    setCloneWizardState,
    showSaveConfirmationDialog,
    isNewRecord,
  }: CloneRecordWizzardProps,
  ref: Ref<CloneWizardRef>
) => {
  const [key, setKey] = useState(0);
  const wizardRef = useRef<WizardRef>(null);
  const [firstStepError, setFirstStepError] = useState(false);
  const [secondStepError, setSecondStepError] = useState(false);
  const [disableFinishButton, setDisableFinishButton] = useState(false);

  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    open: () => {
      wizardRef.current?.open();
    },
  }));

  useEffect(() => {
    if (!(cloneWizardState.users.length > 0 || cloneWizardState.groups.length > 0)) {
      return setDisableFinishButton(true);
    } else if (!cloneWizardTargetWorkflowStep) {
      return setDisableFinishButton(!cloneWizardState.status);
    } else {
      return setDisableFinishButton(false);
    }
  }, [cloneWizardState, cloneWizardTargetWorkflowStep]);

  const resetState = () => {
    setKey((prev) => prev + 1);
    setCloneWizardState(initialCloneWizardState);
  };

  const onFinish = () => {
    resetState();
    onClick();
  };

  const validateFirstStep = () => {
    setFirstStepError(!cloneWizardState.status);
    return Boolean(cloneWizardState.status);
  };

  const validateSecondStep = () => {
    setSecondStepError(!(cloneWizardState.users.length > 0 || cloneWizardState.groups.length > 0));
    return cloneWizardState.users.length > 0 || cloneWizardState.groups.length > 0;
  };

  const onButtonClick = () => {
    if (isNewRecord) {
      showSaveConfirmationDialog();
    } else {
      wizardRef.current?.open();
    }
  };

  return (
    <>
      <Wizard
        key={key}
        ref={wizardRef}
        title={t('clone-records')}
        onCancel={resetState}
        onFinish={onFinish}
        disableFinishButton={disableFinishButton}
      >
        <WizardStep label={t('status')} hideStep={Boolean(cloneWizardTargetWorkflowStep)} onNext={validateFirstStep}>
          <FirstStep
            formId={formId}
            setCloneWizardState={setCloneWizardState}
            moduleFormCloneTargetId={moduleFormCloneTargetId}
            error={firstStepError}
            setFirstStepError={setFirstStepError}
          />
        </WizardStep>
        <WizardStep label={t('assignment')} onNext={validateSecondStep}>
          <SecondStep
            formId={formId}
            cloneWizardState={cloneWizardState}
            setCloneWizardState={setCloneWizardState}
            moduleFormCloneTargetId={moduleFormCloneTargetId}
            cloneWizardTargetPersonField={cloneWizardTargetPersonField}
            error={secondStepError}
            setSecondStepError={setSecondStepError}
          />
        </WizardStep>
        <WizardStep label={t('attachments')} hideStep={!allowUserAttachmentCloning}>
          <ThirdStep setCloneWizardState={setCloneWizardState} cloneWizardState={cloneWizardState} />
        </WizardStep>
      </Wizard>
      <WrapperStyledButtonField onClick={onButtonClick} disabled={disabled} buttonCaption={buttonCaption} icon="Copy" />
    </>
  );
};

export default React.forwardRef(CloneRecordWizzard);
